import React, { useState, Children, memo } from 'react';
import { FilterIcon, SearchIcon, MicIcon, LayoutGridIcon, LayoutListIcon } from 'lucide-react';
import { ProfileCard } from '../../components/Directory/ProfileCard';
import { mapNarratorToProfileCard } from '../../utils/profileCardMappers';
import { useDarkMode } from '../../context/DarkModeContext';
import BackButton from '../../components/UI/BackButton';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { useDarkClasses } from '../../hooks/useDarkClasses';
// Mock data for narrators
const narrators = [{
  id: '1',
  name: 'James Anderson',
  slug: 'james-anderson',
  avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Deep, resonant voice perfect for mystery and thriller genres. Over 50 audiobooks narrated.',
  voiceTypes: ['Deep', 'Resonant', 'Authoritative'],
  genres: ['Mystery', 'Thriller', 'Non-Fiction'],
  rating: 4.9,
  bookCount: 52,
  featured: true,
  location: 'Chicago, IL',
  accentsProficient: ['American', 'British', 'Scottish'],
  sampleUrl: '#'
}, {
  id: '2',
  name: 'Emma Clarke',
  slug: 'emma-clarke',
  avatar: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Versatile narrator with a warm, engaging tone. Specializes in romance and young adult fiction.',
  voiceTypes: ['Warm', 'Youthful', 'Versatile'],
  genres: ['Romance', 'Young Adult', 'Fantasy'],
  rating: 4.8,
  bookCount: 38,
  featured: true,
  location: 'London, UK',
  accentsProficient: ['British', 'American', 'Australian', 'Irish'],
  sampleUrl: '#'
}, {
  id: '3',
  name: 'Michael Chen',
  slug: 'michael-chen',
  avatar: 'https://images.unsplash.com/photo-1566492031773-4f4e44671857?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Technical narrator with clear diction and precise delivery. Excellent for science fiction and technical non-fiction.',
  voiceTypes: ['Clear', 'Precise', 'Articulate'],
  genres: ['Science Fiction', 'Technical Non-Fiction', 'Academic'],
  rating: 4.7,
  bookCount: 25,
  featured: false,
  location: 'San Francisco, CA',
  accentsProficient: ['American', 'Neutral'],
  sampleUrl: '#'
}, {
  id: '4',
  name: 'Sophia Rodriguez',
  slug: 'sophia-rodriguez',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: "Dramatic and emotional narrator with a flair for character voices. Perfect for fantasy epics and children's books.",
  voiceTypes: ['Dramatic', 'Character', 'Expressive'],
  genres: ['Fantasy', "Children's", 'Adventure'],
  rating: 4.9,
  bookCount: 42,
  featured: true,
  location: 'Miami, FL',
  accentsProficient: ['American', 'Spanish', 'Various Character Voices'],
  sampleUrl: '#'
}, {
  id: '5',
  name: 'Daniel Jackson',
  slug: 'daniel-jackson',
  avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Smooth, soothing voice with a talent for literary fiction and memoirs. Brings subtle emotion to every performance.',
  voiceTypes: ['Smooth', 'Soothing', 'Contemplative'],
  genres: ['Literary Fiction', 'Memoir', 'Philosophy'],
  rating: 4.8,
  bookCount: 31,
  featured: false,
  location: 'Portland, OR',
  accentsProficient: ['American', 'British', 'French'],
  sampleUrl: '#'
}];
const NarratorDirectory = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedVoiceTypes, setSelectedVoiceTypes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('name-asc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  // Sidebar states
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const allGenres = Array.from(new Set(narrators.flatMap(narrator => narrator.genres))).sort();
  const allVoiceTypes = Array.from(new Set(narrators.flatMap(narrator => narrator.voiceTypes))).sort();
  const filteredNarrators = narrators.filter(narrator => {
    const matchesSearch = searchTerm === '' || narrator.name.toLowerCase().includes(searchTerm.toLowerCase()) || narrator.bio.toLowerCase().includes(searchTerm.toLowerCase()) || narrator.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGenres = selectedGenres.length === 0 || selectedGenres.some(genre => narrator.genres.includes(genre));
    const matchesVoiceTypes = selectedVoiceTypes.length === 0 || selectedVoiceTypes.some(type => narrator.voiceTypes.includes(type));
    return matchesSearch && matchesGenres && matchesVoiceTypes;
  });
  // Sort logic
  filteredNarrators.sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'books-desc':
        return b.bookCount - a.bookCount;
      case 'rating-desc':
        return b.rating - a.rating;
      default:
        return 0;
    }
  });
  const toggleGenre = (genre: string) => {
    setSelectedGenres(prev => prev.includes(genre) ? prev.filter(g => g !== genre) : [...prev, genre]);
  };
  const toggleVoiceType = (type: string) => {
    setSelectedVoiceTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedVoiceTypes([]);
    setSortBy('name-asc');
    setSearchTerm('');
  };
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
    document.body.style.overflow = !mobileFiltersOpen ? 'hidden' : 'auto';
  };
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'name-asc',
      label: 'A-Z'
    }, {
      value: 'name-desc',
      label: 'Z-A'
    }, {
      value: 'books-desc',
      label: 'Most Books'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'genres',
    title: 'Genres',
    type: 'checkbox',
    options: allGenres.map(genre => ({
      value: genre,
      label: genre
    })),
    value: selectedGenres,
    onChange: toggleGenre
  }, {
    id: 'voiceTypes',
    title: 'Voice Types',
    type: 'checkbox',
    options: allVoiceTypes.map(type => ({
      value: type,
      label: type
    })),
    value: selectedVoiceTypes,
    onChange: toggleVoiceType
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-11375" data-mp="2hhpjmw6j3qhv">
      <div className={`pt-24 md:pt-8 pb-6 px-4 sm:px-6 lg:px-8`} data-id="element-11376" data-mp="2hihgeabv3u9q">
        <div className="max-w-7xl mx-auto" data-id="element-11377" data-mp="2dgniuawyr6ds">
          <div className="mb-4" data-id="element-11378" data-mp="3a5bwyw5k69hx">
            <BackButton data-id="element-11379" data-mp="3a4aalvj3e0kw" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-11380" data-mp="3a7fzparq12gm">
            Narrator Directory
          </h1>
          <p className={`mt-2 ${dk.textSecondary}`} data-id="element-11381" data-mp="358lbtuewcnpn">
            Find professional voice talent for your audiobook projects
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-11382" data-mp="2wdfap8qkmttg">
        {/* Mobile Filter Button */}
        <div className="lg:hidden mb-6" data-id="element-11383" data-mp="3sx4h012ngkdi">
          <button onClick={toggleMobileFilters} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md transition-colors ${dk.card}`} data-id="element-11384" data-mp="3o5d0ykh4dx1m">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-11385" data-mp="ge7ju4t44fmo" />
            <span data-id="element-11386" data-mp="gg0t7rv41dyd">Filters & Sort</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-11387" data-mp="1qcrwqmxifan">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} data-id="element-11388" data-mp="6o4q22e62xna">
            <FilterSidebar title="Filters" sections={filterSections} onReset={resetFilters} data-id="element-11389" data-mp="zc0kf0g9kdgw" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-11390" data-mp="pdb43c40n12r">
            {/* Search and View Toggle */}
            <div className={`rounded-xl shadow-sm p-4 mb-6 border ${dk.card}`} data-id="element-11391" data-mp="354qlf7viva6o">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-11392" data-mp="3a6fy0rye7vwv">
                <div className="relative flex-1" data-id="element-11393" data-mp="1s78i7jrsqtap">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-11394" data-mp="1s5x68wo76f8i" />

                  <input type="text" placeholder="Search narrators by name, location, or keywords..." className={`pl-10 pr-4 py-2.5 w-full rounded-lg border focus:outline-none focus:ring-2 focus:ring-amber-500 transition-colors ${dk.input}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-11395" data-mp="218y98ohrnq7y" />
                </div>
                <div className={`flex rounded-lg p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-11396" data-mp="1iavia45nrj7i">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? darkMode ? 'bg-gray-600 text-amber-400 shadow-sm' : 'bg-white text-amber-600 shadow-sm' : dk.textMuted}`} aria-label="Grid view" data-id="element-11397" data-mp="pt1bcary9vyl">
                    <LayoutGridIcon className="h-5 w-5" data-id="element-11398" data-mp="1jgp7mz1u6unl" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? darkMode ? 'bg-gray-600 text-amber-400 shadow-sm' : 'bg-white text-amber-600 shadow-sm' : dk.textMuted}`} aria-label="List view" data-id="element-11399" data-mp="1fcr84n8lnhu0">
                    <LayoutListIcon className="h-5 w-5" data-id="element-11400" data-mp="10mte3qnznxww" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="mb-6" data-id="element-11401" data-mp="rpsv5zjous0u">
              <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-11402" data-mp="rmes0p37aesc">
                {filteredNarrators.length} narrator
                {filteredNarrators.length !== 1 ? 's' : ''} found
              </h2>
            </div>

            {/* Narrators Grid/List */}
            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 gap-4'}`} data-id="element-11403" data-mp="2tcvp19viieh2">
              {filteredNarrators.map(narrator => <ProfileCard key={narrator.id} data={mapNarratorToProfileCard(narrator, {
              showPricing: true
            })} viewMode={viewMode} data-id="element-11404" data-mp="339gvlzn6jxn0" />)}
            </div>

            {filteredNarrators.length === 0 && <div className={`text-center py-16 rounded-xl border-2 border-dashed ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-11405" data-mp="2jnubrrgcqb6s">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-11406" data-mp="1rwef14l2z82u">
                  <MicIcon className={`h-8 w-8 ${dk.textFaint}`} data-id="element-11407" data-mp="1vvn96ak197yx" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-11408" data-mp="2z6ucs3yd5t3j">
                  No narrators found
                </h3>
                <p className={dk.textMuted} data-id="element-11409" data-mp="38ygw6nkuuyhd">
                  Try adjusting your search terms or selected filters
                </p>
                <button onClick={resetFilters} className="mt-4 text-amber-600 hover:text-amber-500 font-medium text-sm" data-id="element-11410" data-mp="340ylarna296q">
                  Clear all filters
                </button>
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default NarratorDirectory;